import styled, { css, keyframes } from 'styled-components'
import { tokens } from 'styling/tokens'
import { OtherText } from 'styling/Typography'
import { OpsEventSeverity } from 'types/operationsControl.interface'

const SEVERITY_COLOR: { [k in OpsEventSeverity]: string } = {
  critical: tokens.status.danger.fg,
  warning: tokens.status.warning.fg,
  info: tokens.status.info.fg,
  success: tokens.status.success.fg,
}

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
`

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
`

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
`

export const FilterLabel = styled.span`
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${tokens.text.muted};
`

export const SearchWrap = styled.div`
  margin-left: auto;
  width: 18rem;
  max-width: 100%;
`

export const StreamWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.5rem;
  width: 100%;
`

export const StreamLine = styled.div`
  position: absolute;
  top: 0.625rem;
  bottom: 0.625rem;
  left: calc(5.25rem - 0.5px);
  width: 1px;
  background: ${tokens.border.hairline};
`

export const EventRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 4rem 1.5rem minmax(0, 1fr);
  column-gap: 0.5rem;
`

export const TimeCell = styled.span`
  padding-top: 0.625rem;
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: ${tokens.font.label};
  line-height: 1rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  color: ${tokens.text.muted};
`

export const DotCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 0.75rem;
`

const criticalPulse = keyframes`
  0% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0 ${tokens.status.danger.bg};
  }
  70% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0.375rem transparent;
  }
  100% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0 transparent;
  }
`

export const SeverityDot = styled.span<{ $tone: OpsEventSeverity }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${({ $tone }) => SEVERITY_COLOR[$tone]};
  box-shadow: 0 0 0.5rem ${({ $tone }) => SEVERITY_COLOR[$tone]};
  ${({ $tone }) =>
    $tone === 'critical' &&
    css`
      animation: ${criticalPulse} 2s ${tokens.motion.ease} infinite;
    `}
`

export const EventCard = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.control};
  background: transparent;
  transition: background ${tokens.motion.duration} ${tokens.motion.ease},
    border-color ${tokens.motion.duration} ${tokens.motion.ease};
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${tokens.surface.glass};
        border-color: ${tokens.border.strong};
      }
    `}
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const OpenHint = styled.span`
  margin-left: auto;
  font-size: ${tokens.font.label};
  font-weight: 600;
  white-space: nowrap;
  color: ${tokens.text.muted};
  transition: color ${tokens.motion.duration} ${tokens.motion.ease};

  ${EventCard}:hover & {
    color: ${tokens.accent.base};
  }
`

export const TapHint = styled(OtherText)`
  margin-top: 0.5rem;
  width: 100%;
`

export const KindChip = styled.span`
  padding: 0.125rem 0.375rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.chip};
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${tokens.text.muted};
`

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const DepartmentTag = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const MarkerRow = styled.div`
  display: grid;
  grid-template-columns: 4rem 1.5rem minmax(0, 1fr);
  column-gap: 0.5rem;
  align-items: center;
  min-height: 1.25rem;
`

export const MarkerDot = styled.span<{ $muted?: boolean }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 999px;
  margin: 0 auto;
  background: ${({ $muted }) => ($muted ? tokens.text.muted : tokens.accent.base)};
  box-shadow: ${({ $muted }) => ($muted ? 'none' : `0 0 0.5rem ${tokens.accent.glowTint}`)};
`

export const MarkerLabel = styled.span<{ $muted?: boolean }>`
  font-size: ${tokens.font.label};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $muted }) => ($muted ? tokens.text.muted : tokens.accent.onDark)};
`

export const EmptyStreamWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
  margin-top: 1.5rem;
  width: 100%;
  border: 1px dashed ${tokens.border.strong};
  border-radius: ${tokens.radius.card};
  background: ${tokens.surface.inset};
`

export const GroupAffordance = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.text.muted};
  transition: color ${tokens.motion.duration} ${tokens.motion.ease};

  ${EventCard}:hover & {
    color: ${tokens.accent.base};
  }
`

export const GroupChevron = styled.span<{ $expanded?: boolean }>`
  display: inline-flex;
  font-size: ${tokens.font.body};
  line-height: 1;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
  transition: transform ${tokens.motion.duration} ${tokens.motion.ease};
`

export const GroupChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
